import React, { useEffect, useRef } from 'react';
import { createChart, ColorType } from 'lightweight-charts';
import { Paper, Typography, Box } from '@mui/material';

export const TechnicalChart = ({ data, indicatorsVisible }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const savedLogicalRangeRef = useRef(null);

  useEffect(() => {
    if (!chartContainerRef.current || !data || data.length === 0) return;

    // Preserve zoom level & scroll position before re-rendering
    if (chartRef.current) {
      try {
        const currentRange = chartRef.current.timeScale().getVisibleLogicalRange();
        if (currentRange) {
          savedLogicalRangeRef.current = currentRange;
        }
      } catch (err) {
        console.error("Failed to capture range:", err);
      }
    }

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 520,
      layout: { 
        background: { type: ColorType.Solid, color: '#FFFFFF' }, 
        textColor: '#334155' 
      },
      grid: { 
        vertLines: { color: '#E2E8F0' }, 
        horzLines: { color: '#E2E8F0' } 
      },
      timeScale: { borderColor: '#CBD5E1' },
    });

    chartRef.current = chart;

    // Set Candlesticks
    const candleSeries = chart.addCandlestickSeries({
      upColor: '#10B981', 
      downColor: '#EF4444', 
      borderVisible: false, 
      wickUpColor: '#10B981', 
      wickDownColor: '#EF4444'
    });
    candleSeries.setData(data.map(d => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close })));

    // Add Volume Histogram Series (scaled at the bottom of the main pane)
    const volumeSeries = chart.addHistogramSeries({
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '', // Overlay on the main pane
    });

    // Configure price scale so volume only occupies the bottom 20%
    chart.priceScale('').applyOptions({
      scaleMargins: {
        top: 0.8,
        bottom: 0,
      },
    });

    // Set Volume Data with dynamic coloring based on price movement
    const volumeData = data.map(d => ({
      time: d.time,
      value: d.volume,
      color: d.close >= d.open ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'
    }));
    volumeSeries.setData(volumeData);

    // Indicator Toggles
    if (indicatorsVisible.sma20) {
      const sma20Series = chart.addLineSeries({ color: '#2563EB', lineWidth: 1.5 });
      sma20Series.setData(data.filter(d => d.sma_20).map(d => ({ time: d.time, value: d.sma_20 })));
    }
    if (indicatorsVisible.sma50) {
      const sma50Series = chart.addLineSeries({ color: '#D97706', lineWidth: 1.5 });
      sma50Series.setData(data.filter(d => d.sma_50).map(d => ({ time: d.time, value: d.sma_50 })));
    }
    if (indicatorsVisible.sma200) {
      const sma200Series = chart.addLineSeries({ color: '#DB2777', lineWidth: 2 });
      sma200Series.setData(data.filter(d => d.sma_200).map(d => ({ time: d.time, value: d.sma_200 })));
    }
    if (indicatorsVisible.bollinger) {
      const bbUpper = chart.addLineSeries({ color: 'rgba(100, 116, 139, 0.5)', lineWidth: 1 });
      const bbLower = chart.addLineSeries({ color: 'rgba(100, 116, 139, 0.5)', lineWidth: 1 });
      bbUpper.setData(data.filter(d => d.bb_upper).map(d => ({ time: d.time, value: d.bb_upper })));
      bbLower.setData(data.filter(d => d.bb_lower).map(d => ({ time: d.time, value: d.bb_lower })));
    }

    // Set Signal Markers
    const markers = [];
    data.forEach(d => {
      if (d.signal === 'BUY') markers.push({ time: d.time, position: 'belowBar', color: '#10B981', shape: 'arrowUp', text: 'BUY' });
      else if (d.signal === 'SELL') markers.push({ time: d.time, position: 'aboveBar', color: '#EF4444', shape: 'arrowDown', text: 'SELL' });
    });
    candleSeries.setMarkers(markers);

    // Restore saved zoom/pan position if it exists
    if (savedLogicalRangeRef.current) {
      chart.timeScale().setVisibleLogicalRange(savedLogicalRangeRef.current);
    }

    const handleResize = () => chart.applyOptions({ width: chartContainerRef.current.clientWidth });
    window.addEventListener('resize', handleResize);
    
    return () => { 
      window.removeEventListener('resize', handleResize); 
      chart.remove(); 
    };
  }, [data, indicatorsVisible]);

  return (
    <Paper elevation={0} sx={{ p: 2, backgroundColor: '#FFFFFF', borderRadius: 3, border: '1px solid #E2E8F0' }}>
      <Typography variant="h6" sx={{ color: '#0F172A', mb: 1, fontWeight: 600 }}>
        Interactive Technical Chart Execution
      </Typography>
      
      {/* Container with relative positioning for absolute legend positioning */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        
        {/* Dynamic Legend Overlay */}
        {Object.values(indicatorsVisible).some(Boolean) && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 12, 
              left: 12, 
              zIndex: 10, 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1.5, 
              backgroundColor: 'rgba(255, 255, 255, 0.9)', 
              backdropFilter: 'blur(4px)',
              p: '6px 12px', 
              borderRadius: 1.5, 
              border: '1px solid #E2E8F0',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.05)'
            }}
          >
            {indicatorsVisible.sma20 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 14, height: 0, borderBottom: '2px solid #2563EB' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563EB' }}>
                  SMA 20
                </Typography>
              </Box>
            )}

            {indicatorsVisible.sma50 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 14, height: 0, borderBottom: '2px solid #D97706' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#D97706' }}>
                  SMA 50
                </Typography>
              </Box>
            )}

            {indicatorsVisible.sma200 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 14, height: 0, borderBottom: '2.5px solid #DB2777' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#DB2777' }}>
                  SMA 200
                </Typography>
              </Box>
            )}

            {indicatorsVisible.bollinger && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <Box sx={{ width: 14, height: 0, borderBottom: '1.5px solid #64748B' }} />
                  <Box sx={{ width: 14, height: 0, borderBottom: '1.5px solid #64748B' }} />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B' }}>
                  Bollinger Bands
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Chart Canvas */}
        <div ref={chartContainerRef} style={{ width: '100%', position: 'relative' }} />
      </Box>
    </Paper>
  );
};